import { useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { RaisePurchaseOrder } from '@/components/admin/raise-purchase-order';
import { Badge, Callout, buttonClass } from '@/components/ui';
import { adminLowStockQuery } from '@/queries';
import type { LowStockRow } from '@/lib/stock-types';
import { suggestedQty } from '@/lib/reorder';

/**
 * What needs reordering. Spec 003 US-2, task T058, FR-109.
 *
 * Two decisions worth stating.
 *
 * **It measures `free`, not `on_hand`.** Stock promised to an order cannot
 * cover the next one, so a reorder point read against on hand orders late by
 * exactly the amount already sold.
 *
 * **It is computed on the server.** The stock grid has a low filter, and a
 * filter is allowed to work on the rows in front of it. A reorder list is not:
 * with 1,896 variants the grid pages, and a list that quietly stops at the page
 * boundary is worse than no list, because it looks complete.
 *
 * Grouping is by preferred supplier, and an item without one appears under a
 * heading that says so. Nothing is invented to make the grouping tidy.
 */

function Row({ row, uomOf }: { row: LowStockRow; uomOf: string }) {
  const out = Number(row.free) <= 0;
  return (
    <div
      className="grid items-center gap-3 border-b border-line px-[14px] text-[12.5px] last:border-0"
      style={{ gridTemplateColumns: '1fr 100px 110px 120px' }}
    >
      <Link
        to="/admin/stock/$itemId"
        params={{ itemId: row.id }}
        className="truncate hover:underline"
      >
        <span className="font-mono text-[13px] text-muted">{row.code}</span> {row.name}
      </Link>
      <span className="text-right">
        {out ? <Badge tone="warn">none</Badge> : Number(row.free)}
      </span>
      <span className="text-right text-muted">
        {Number(row.reorder_point)} {uomOf}
      </span>
      {/* Spec 014, FR-1411: up to the maximum less free and on order, or the
          reorder quantity when the item has no maximum. */}
      <span className="text-right font-semibold" title={row.max_level != null ? `Up to ${Number(row.max_level)}` : undefined}>
        {suggestedQty(row) ? `${suggestedQty(row)} ${uomOf}` : '—'}
      </span>
    </div>
  );
}

export default function AdminReorderPage() {
  const data = useSuspenseQuery(adminLowStockQuery).data;
  const { rows, suppliers } = data;
  // Counted here if the answer did not carry it. The server always sends it,
  // and a page that dies on one missing field is the failure this repository
  // keeps repeating (R-181, R-192, R-196). The fallback is the same arithmetic,
  // not a guess.
  const summary = data.summary ?? {
    short: rows.length,
    covered: rows.filter((r) => (r.covered_short ?? 0) === 0).length,
    to_buy: rows.filter((r) => (r.covered_short ?? 0) > 0).length,
  };
  const qc = useQueryClient();
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Turn the shortfall into draft purchase orders. G1.
   *
   * The list told somebody what was short and offered no way to act, so they
   * read it, opened purchase orders, and retyped what they had just read.
   *
   * DRAFTS, never sent. The system prepares; a person sends. Buying without a
   * human is how a shop ends up with four thousand of something. R-206.
   */
  const raise = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/stock/reorder', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
      });
      const body = (await res.json().catch(() => null)) as {
        orders?: { ref: string; lines: number }[];
        no_supplier?: { code: string }[];
        error?: { message?: string };
      } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'Could not raise the orders.');
      return body!;
    },
    onSuccess: (body) => {
      setError(null);
      const made = body.orders ?? [];
      const missing = body.no_supplier ?? [];
      setResult(
        made.length === 0
          ? 'Nothing needed ordering that is not already on its way.'
          : `${made.length} draft order(s) raised: ${made.map((o) => o.ref).join(', ')}. `
            + 'They are drafts. Check them and send them.'
            + (missing.length
              ? ` ${missing.length} item(s) have no supplier set and were left out: `
                + `${missing.map((m) => m.code).join(', ')}.`
              : ''),
      );
      void qc.invalidateQueries({ queryKey: ['admin', 'stock', 'low'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'purchase-orders'] });
    },
    onError: (e: Error) => { setResult(null); setError(e.message); },
  });

  const nameOf = new Map(suppliers.map((s) => [s.id, s.name]));
  const groups = new Map<string, LowStockRow[]>();
  for (const r of rows) {
    const key = r.preferred_supplier_id ?? '';
    groups.set(key, [...(groups.get(key) ?? []), r]);
  }

  const out = rows.filter((r) => Number(r.free) <= 0).length;

  return (
    <div>
      <div className="mb-[14px] flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Reorder</h1>
          <p className="mt-1 text-[12.5px] text-muted">
            Every Stock line at or below its minimum, measured on what is free
            rather than what is on the shelf. On request and Run-out items are not
            reordered.
            {summary.covered > 0 && (
              <> {summary.covered} of them {summary.covered === 1 ? 'is' : 'are'} already
              covered by a delivery on its way.</>
            )}
          </p>
        </div>
        {summary.to_buy > 0 && (
          <button
            type="button"
            className={buttonClass('primary', 'sm')}
            disabled={raise.isPending}
            onClick={() => raise.mutate()}
          >
            {raise.isPending ? 'Raising' : `Raise draft orders for ${summary.to_buy}`}
          </button>
        )}
      </div>

      {result && <Callout tone="ok" title={result} className="mb-5" />}
      {error && <Callout tone="danger" title={error} className="mb-5" />}

      {out > 0 && (
        <div className="mb-5 rounded-[12px] border border-line bg-panel p-4 text-[14px]">
          <strong>{out} {out === 1 ? 'item has' : 'items have'} nothing free.</strong>{' '}
          Anything already promised is counted as gone, so these cannot cover a
          new job today.
        </div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-panel p-8 text-center">
          <p className="text-[16px] font-semibold">Nothing needs reordering.</p>
          <p className="mx-auto mt-2 max-w-[50ch] text-[14px] text-muted">
            This list only shows items that have a reorder point set. An item
            with no reorder point can run out without appearing here, so setting
            them is worth doing as the stock is loaded.
          </p>
        </div>
      ) : (
        [...groups.entries()].map(([supplierId, group]) => (
          <section key={supplierId || 'none'} className="mb-6">
            <h2 className="mb-2 text-[13px] font-bold uppercase tracking-[0.08em] text-muted [overflow-wrap:anywhere]">
              {supplierId
                ? (nameOf.get(supplierId) ?? 'Supplier')
                : 'No supplier set yet'}
            </h2>

            {/* An item with no supplier cannot be ordered from one, and
                inventing a supplier to make the button appear would be worse
                than not offering it. D018. */}
            {supplierId && (
              <RaisePurchaseOrder
                supplierId={supplierId}
                supplierName={nameOf.get(supplierId) ?? 'this supplier'}
                rows={group}
              />
            )}

            {/* The table scrolls in its own box on a phone, and the page does
                not. Four columns, three of them fixed, are 394 px before any
                text: 41 px wider than a 390 px phone once real items needed
                reordering (pipeline 150, 30 October). `relative`, so nothing
                inside escapes the box (tracker #8). */}
            <div className="relative overflow-x-auto rounded-[10px] border border-line bg-panel">
              <div className="min-w-[560px]">
              <div
                className="grid gap-3 border-b border-line px-4 py-2 text-[12px] font-semibold
                           uppercase tracking-wide text-muted"
                style={{ gridTemplateColumns: '1fr 100px 110px 120px' }}
              >
                <span>Item</span>
                <span className="text-right">Free</span>
                <span className="text-right">Minimum</span>
                <span className="text-right">Order</span>
              </div>
              {group.map((r) => (
                <Row key={r.id} row={r} uomOf={r.base_uom} />
              ))}
              </div>
            </div>
          </section>
        ))
      )}
    </div>
  );
}
